'use client'

// ============================================================
// Isca do Jhow (fase 1, SDD-jhow-isca-fase1)
// ============================================================
// A conversa fica so no navegador, assinada pelo servidor (ADR-036). O Jhow ajuda
// a montar a primeira resposta e propoe a versao; a pessoa confirma no botao "É
// isso"; ai ele explica e convida, e o e-mail so aparece depois do convite. Nenhuma animacao esconde texto, e a caixa de texto tem cor inline
// (a camada base do Tailwind v4 vence a utility e deixava o texto invisivel).
// ============================================================

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import Link from 'next/link'
import { ABERTURA_ISCA, LIMITES, type ConversaAssinada, type MensagemIsca } from '@/lib/jhow/isca/conversa'
import { apagarConversaLocal, lerConversaLocal, salvarConversaLocal } from '@/lib/jhow/isca/local'

const COR_CAMPO = { color: '#1A1A1D', backgroundColor: '#FFFFFF' } as const
const ENTRAR_DEPOIS = '/magic?returnUrl=%2Fapp%2Fjornada%2Fposicionamento'

export function IscaChat() {
  const [conversa, setConversa] = useState<ConversaAssinada | null>(null)
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [limite, setLimite] = useState(false)
  const [email, setEmail] = useState('')
  const [emailEstado, setEmailEstado] = useState<'nada' | 'enviando' | 'enviado'>('nada')
  const [emailErro, setEmailErro] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)
  const fimRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const salva = lerConversaLocal()
    if (salva) setConversa(salva)
  }, [])

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'nearest' })
  }, [conversa, enviando, emailEstado])

  const mensagens: MensagemIsca[] = conversa?.mensagens ?? [{ papel: 'jhow', texto: ABERTURA_ISCA }]
  const resposta = conversa?.resposta ?? null
  const mostrarEmail = !!resposta && (conversa?.fase === 'convite' || limite)
  const podeEscrever = !limite && emailEstado !== 'enviado'

  async function enviar(confirmarProposta = false) {
    const t = texto.trim()
    if (enviando || (!confirmarProposta && !t)) return
    setEnviando(true)
    setAviso(null)
    try {
      const res = await fetch('/api/jhow/isca', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(confirmarProposta ? { conversa, confirmar: true } : { conversa, texto: t }),
      })
      const data = (await res.json().catch(() => ({}))) as { conversa?: ConversaAssinada; erro?: string }
      if (res.ok && data.conversa) {
        setConversa(data.conversa)
        salvarConversaLocal(data.conversa)
        if (!confirmarProposta) setTexto('')
        return
      }
      if (res.status === 409 && data.erro === 'conversa_invalida') {
        apagarConversaLocal()
        setConversa(null)
        setAviso('A conversa anterior venceu. Vamos recomeçar: me conta de novo, do seu jeito.')
        return
      }
      if (res.status === 409 && data.erro === 'nada_a_confirmar') {
        setAviso('Não achei a versão para confirmar. Escreve de novo o que você faz, do seu jeito.')
        return
      }
      if (res.status === 409 && data.erro === 'limite') {
        setLimite(true)
        return
      }
      if (res.status === 429) {
        setAviso('Muitas mensagens em pouco tempo. Espera uns minutos e tenta de novo.')
        return
      }
      if (res.status === 400) {
        setAviso(`Escreve até ${LIMITES.caracteresPorMensagem.toLocaleString('pt-BR')} caracteres por mensagem.`)
        return
      }
      setAviso('O Jhow está ocupado agora. Tenta de novo em instantes: sua mensagem continua aqui.')
    } catch {
      setAviso('Sem conexão agora. Tenta de novo em instantes: sua mensagem continua aqui.')
    } finally {
      setEnviando(false)
    }
  }

  function aoTeclar(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void enviar(false)
    }
  }

  async function pedirLink(e: FormEvent) {
    e.preventDefault()
    if (!conversa || emailEstado === 'enviando') return
    setEmailEstado('enviando')
    setEmailErro(null)
    try {
      const res = await fetch('/api/jhow/isca/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversa, email }),
      })
      if (res.ok) {
        setEmailEstado('enviado')
        return
      }
      setEmailEstado('nada')
      if (res.status === 400) setEmailErro('Confere o e-mail: parece que faltou alguma coisa.')
      else if (res.status === 409) setEmailErro('A conversa venceu. Recarrega a página e tenta de novo.')
      else if (res.status === 429) setEmailErro('Muitos pedidos agora. Espera uns minutos.')
      else setEmailErro('Não deu para enviar agora. Tenta de novo em instantes.')
    } catch {
      setEmailEstado('nada')
      setEmailErro('Sem conexão agora. Tenta de novo em instantes.')
    }
  }

  async function copiar() {
    if (!resposta) return
    try {
      await navigator.clipboard.writeText(resposta)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2500)
    } catch {
      setCopiado(false)
    }
  }

  return (
    <section aria-label="Conversa com o Jhow" className="rounded-2xl border border-ink-7 bg-paper shadow-soft">
      <div className="max-h-[65vh] space-y-3 overflow-y-auto px-4 pb-2 pt-4" aria-live="polite">
        {mensagens.map((m, i) => (
          <div key={i} className={m.papel === 'jhow' ? 'flex justify-start' : 'flex justify-end'}>
            <div
              className={
                m.papel === 'jhow'
                  ? 'max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-ink-8 px-4 py-3 text-sm leading-relaxed text-ink-1'
                  : 'max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-brand-grad px-4 py-3 text-sm leading-relaxed text-white'
              }
            >
              {m.papel === 'jhow' && <span className="mb-1 block text-[11px] font-bold tracking-wide text-ink-4">Jhow</span>}
              {m.texto}
            </div>
          </div>
        ))}

        {conversa?.fase === 'montando' && conversa.proposta && (
          <div className="rounded-xl border border-brand-100 bg-brand-50 p-4">
            <p className="text-xs font-bold text-ink-3">A sua resposta, do jeito que eu entendi</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink-0">{conversa.proposta}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void enviar(true)}
                disabled={enviando}
                className="rounded-full bg-brand-grad px-5 py-2 text-sm font-bold text-white disabled:opacity-60"
              >
                É isso
              </button>
              <span className="text-xs text-ink-4">ou escreva abaixo o que quer mudar</span>
            </div>
          </div>
        )}

        {enviando && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-tl-sm bg-ink-8 px-4 py-3 text-sm text-ink-4">O Jhow está escrevendo…</div>
          </div>
        )}

        {resposta && (
          <div className="rounded-xl border border-brand-100 bg-brand-50 p-4">
            <p className="text-xs font-bold text-ink-3">Sua resposta</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink-0">{resposta}</p>
            <button type="button" onClick={copiar} className="mt-2 text-xs font-semibold text-brand-600 underline">
              {copiado ? 'Copiada' : 'Copiar'}
            </button>
          </div>
        )}

        {mostrarEmail &&
          (emailEstado === 'enviado' ? (
            <div className="rounded-2xl rounded-tl-sm bg-ink-8 px-4 py-3 text-sm text-ink-1">
              Pronto. Veja seu e-mail e abra o link <strong>neste mesmo aparelho</strong>, para continuar de onde paramos.
            </div>
          ) : (
            <form onSubmit={pedirLink} className="rounded-2xl rounded-tl-sm bg-ink-8 px-4 py-3">
              <label htmlFor="isca-email" className="block text-sm text-ink-1">
                Seu e-mail, para eu te mandar o acesso:
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  id="isca-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-w-0 flex-1 rounded-full border border-ink-6 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none"
                  style={COR_CAMPO}
                />
                <button
                  type="submit"
                  disabled={emailEstado === 'enviando'}
                  className="shrink-0 rounded-full bg-brand-grad px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
                >
                  {emailEstado === 'enviando' ? 'Enviando…' : 'Receber o link'}
                </button>
              </div>
              {emailErro && <p className="mt-2 text-xs text-red-700">{emailErro}</p>}
            </form>
          ))}

        {limite && !resposta && (
          <div className="rounded-2xl rounded-tl-sm bg-ink-8 px-4 py-3 text-sm text-ink-1">
            Chegamos no limite desta conversa por aqui. Entra e a gente continua lá dentro:{' '}
            <Link href={ENTRAR_DEPOIS} className="font-semibold underline">
              entrar com meu e-mail
            </Link>
            .
          </div>
        )}
        <div ref={fimRef} />
      </div>

      {podeEscrever && (
        <div className="border-t border-ink-7 p-3">
          <label htmlFor="isca-texto" className="sr-only">
            Sua mensagem para o Jhow
          </label>
          <div className="flex items-end gap-2">
            <textarea
              id="isca-texto"
              rows={2}
              maxLength={LIMITES.caracteresPorMensagem}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={aoTeclar}
              placeholder="Escreva do seu jeito"
              className="min-h-[44px] flex-1 resize-none rounded-xl border border-ink-6 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              style={COR_CAMPO}
            />
            <button
              type="button"
              onClick={() => void enviar(false)}
              disabled={!texto.trim() || enviando}
              className="shrink-0 rounded-full bg-brand-grad px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              Enviar
            </button>
          </div>
          {aviso && (
            <p role="status" className="mt-2 text-xs text-ink-3">
              {aviso}
            </p>
          )}
          <p className="mt-2 text-[11px] leading-snug text-ink-4">
            O que você escreve vai para o Jhow responder e não fica guardado enquanto você não criar a conta.{' '}
            <Link href="/privacidade" className="underline">
              Política de privacidade
            </Link>
            .
          </p>
        </div>
      )}
    </section>
  )
}
